/**
 * Luxury Collection Showcase Carousel Component
 */
if (!customElements.get('luxury-showcase-carousel')) {
  class LuxuryShowcaseCarousel extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.init();
      this.bindShopifyEvents();
    }

    init() {
      this.track = this.querySelector('[data-showcase-track]');
      this.prevBtn = this.querySelector('[data-arrow-prev]');
      this.nextBtn = this.querySelector('[data-arrow-next]');
      this.playBtn = this.querySelector('[data-play-pause]');
      this.paginationContainer = this.querySelector('[data-pagination]');
      this.tabs = Array.from(this.querySelectorAll('[data-tab-filter]'));
      this.cards = Array.from(this.querySelectorAll('.luxury-showcase__card'));
      this.isPlaying = false;
      this.autoplayTimer = null;

      if (!this.track) return;

      this.initTrackNavigation();
      this.initTabs();
      this.initCardSliders();
      this.updatePagination();
    }

    bindShopifyEvents() {
      // In Shopify Theme Editor, re-init when section is updated or block is clicked
      document.addEventListener('shopify:section:load', (e) => {
        if (e.target.contains(this)) {
          this.init();
        }
      });

      document.addEventListener('shopify:block:select', (e) => {
        const blockId = e.detail.blockId;
        const matchingTab = this.tabs.find(tab => tab.getAttribute('data-block-id') === blockId);
        if (matchingTab) {
          matchingTab.click();
        }
      });
    }

    initTrackNavigation() {
      if (this.prevBtn) {
        this.prevBtn.onclick = () => this.scrollTrack('prev');
      }

      if (this.nextBtn) {
        this.nextBtn.onclick = () => this.scrollTrack('next');
      }

      if (this.playBtn) {
        this.playBtn.onclick = () => this.toggleAutoplay();
      }

      // Track scroll event to update pagination dots
      let scrollTimeout;
      this.track.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          this.updateActiveDot();
        }, 40);
      }, { passive: true });
    }

    toggleAutoplay() {
      this.isPlaying = !this.isPlaying;
      const playIcon = this.playBtn?.querySelector('.icon-play');
      const pauseIcon = this.playBtn?.querySelector('.icon-pause');
      if (this.isPlaying) {
        if (playIcon) playIcon.style.display = 'none';
        if (pauseIcon) pauseIcon.style.display = 'block';
        this.startAutoplay();
      } else {
        if (playIcon) playIcon.style.display = 'block';
        if (pauseIcon) pauseIcon.style.display = 'none';
        this.stopAutoplay();
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        const visibleCards = this.cards.filter(c => c.style.display !== 'none');
        if (visibleCards.length <= 1) return;
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        if (this.track.scrollLeft >= maxScroll - 20) {
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.scrollTrack('next');
        }
      }, 3500);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }


    scrollTrack(direction) {
      const visibleCard = this.cards.find(c => c.style.display !== 'none');
      const step = visibleCard ? visibleCard.offsetWidth + 12 : 433;
      const currentScroll = this.track.scrollLeft;
      const target = direction === 'next' ? currentScroll + step : currentScroll - step;

      this.track.scrollTo({
        left: target,
        behavior: 'smooth'
      });
    }

    scrollToIndex(index) {
      const visibleCards = this.cards.filter(card => card.style.display !== 'none');
      if (visibleCards[index]) {
        visibleCards[index].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        });
      }
    }

    updatePagination() {
      if (!this.paginationContainer) return;

      const visibleCards = this.cards.filter(card => card.style.display !== 'none');
      const count = Math.min(visibleCards.length, 6); // Up to 6 dots

      this.paginationContainer.innerHTML = '';
      if (count <= 1) return;

      for (let i = 0; i < count; i++) {
        const dot = document.createElement('span');
        dot.className = `luxury-showcase__dot${i === 0 ? ' is-active' : ''}`;
        dot.setAttribute('data-dot-index', i);
        dot.setAttribute('role', 'button');
        dot.setAttribute('aria-label', `Slide ${i + 1}`);
        dot.onclick = () => this.scrollToIndex(i);
        this.paginationContainer.appendChild(dot);
      }

      this.dots = Array.from(this.paginationContainer.querySelectorAll('[data-dot-index]'));
      this.updateActiveDot();
    }

    updateActiveDot() {
      if (!this.dots || !this.dots.length) return;
      const visibleCards = this.cards.filter(card => card.style.display !== 'none');
      if (!visibleCards.length) return;

      const trackScroll = this.track.scrollLeft;
      const cardWidth = visibleCards[0].offsetWidth + 12;

      let activeIndex = Math.round(trackScroll / cardWidth);
      if (activeIndex < 0) activeIndex = 0;
      if (activeIndex >= this.dots.length) activeIndex = this.dots.length - 1;

      this.dots.forEach((dot, idx) => {
        dot.classList.toggle('is-active', idx === activeIndex);
      });
    }

    initTabs() {
      if (!this.tabs.length) return;

      this.tabs.forEach((tab) => {
        tab.onclick = () => {
          const filter = tab.getAttribute('data-tab-filter');

          // Update active tab styling
          this.tabs.forEach(t => t.classList.remove('is-active'));
          tab.classList.add('is-active');

          // Filter visible cards
          this.filterCards(filter);
        };
      });

      // Filter to initial active tab on load
      const activeTab = this.tabs.find(t => t.classList.contains('is-active')) || this.tabs[0];
      if (activeTab) {
        this.filterCards(activeTab.getAttribute('data-tab-filter'));
      }
    }

    filterCards(filterValue) {
      this.cards.forEach((card) => {
        const cardCategory = card.getAttribute('data-category') || '';
        if (filterValue === 'all' || cardCategory === filterValue || !filterValue) {
          card.style.display = '';
        } else {
          card.style.display = 'none';
        }
      });

      // Reset scroll position and re-calculate pagination
      this.track.scrollTo({ left: 0, behavior: 'instant' });
      this.updatePagination();
    }

    initCardSliders() {
      this.querySelectorAll('.luxury-showcase__media-box').forEach((box) => {
        const mediaTrack = box.querySelector('.luxury-showcase__media-track');
        const prev = box.querySelector('.luxury-showcase__card-nav--prev');
        const next = box.querySelector('.luxury-showcase__card-nav--next');

        if (!mediaTrack) return;

        if (prev) {
          prev.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            mediaTrack.scrollBy({ left: -mediaTrack.offsetWidth, behavior: 'smooth' });
          };
        }

        if (next) {
          next.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            mediaTrack.scrollBy({ left: mediaTrack.offsetWidth, behavior: 'smooth' });
          };
        }
      });
    }
  }

  customElements.define('luxury-showcase-carousel', LuxuryShowcaseCarousel);
}
